import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card } from "../common/Card";
import { ErrorRetry } from "../../common/ErrorRetry";
import { DailyRevenueShimmer } from "../../common/reportDashBShimmer/DailyRevenueShimmer";
import { PieChartShimmer } from "../../common/reportDashBShimmer/PieChartShimmer";
import {
  INSURANCE_CHART_COLORS,
  formatInsuranceMoney,
  formatInsurancePercentage,
} from "./insuranceReportUtils";

export function InsuranceRevenueOverviewSection({
  trendsState,
  breakdownsState,
  monthlyTrend,
  policyTypeBreakdown,
}) {
  return (
    <div>
      <span className="mb-4 text-2xl text-[#1A1A1A]">Revenue Overview</span>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-xl text-[#1A1A1A]">
            Monthly Revenue & Policies Trend
          </span>

          {trendsState.loading ? (
            <DailyRevenueShimmer />
          ) : trendsState.error ? (
            <div className="h-[350px] flex items-center justify-center">
              <ErrorRetry
                message={trendsState.message}
                onRetry={trendsState.onRetry}
              />
            </div>
          ) : monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis yAxisId="left" stroke="#6B7280" />
                <YAxis yAxisId="right" orientation="right" stroke="#6B7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                  formatter={(value, name) =>
                    name === "Revenue (NGN)"
                      ? formatInsuranceMoney(value)
                      : Number(value).toLocaleString()
                  }
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#1E61DC"
                  strokeWidth={3}
                  name="Revenue (NGN)"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="policies"
                  stroke="#10B981"
                  strokeWidth={3}
                  name="Policies"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No revenue trend data available
            </div>
          )}
        </Card>

        {breakdownsState.loading ? (
          <PieChartShimmer />
        ) : policyTypeBreakdown.length > 0 ? (
          <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <span className="mb-6 text-xl text-[#1A1A1A]">
              Policy Type Breakdown
            </span>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={policyTypeBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, sharePercentage }) =>
                    `${name} ${formatInsurancePercentage(sharePercentage)}`
                  }
                >
                  {policyTypeBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        INSURANCE_CHART_COLORS[
                          index % INSURANCE_CHART_COLORS.length
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                  formatter={(value) => formatInsuranceMoney(value)}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <div
              className={`h-[350px] flex items-center justify-center ${
                breakdownsState.error ? "text-red-500" : "text-gray-500"
              }`}
            >
              {breakdownsState.error ? (
                <ErrorRetry
                  message={breakdownsState.message}
                  onRetry={breakdownsState.onRetry}
                />
              ) : (
                "No policy type breakdown available"
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
